import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, EnvelopeIcon, ChevronDownIcon, PencilIcon, CheckIcon } from '@heroicons/react/24/outline';
import { emailAPI, type EmailTemplate, authAPI, extractErrorMessage } from '../services/api';
import { userAssistantConfigService, type UserAssistantConfig } from '../services/userAssistantConfigService';
import { EmailTemplateRichEditor } from './email/EmailTemplateRichEditor';
import { isEmailTemplateBodyEmpty } from '../utils/emailTemplateHtml';

export type EmailLeadTarget = {
  id: string;
  name: string;
  email: string;
};

type SendEmailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  lead: EmailLeadTarget | null;
  setToast: (t: { type: 'success' | 'error'; message: string } | null) => void;
};

function fillPlaceholders(text: string, lead: EmailLeadTarget | null, agentName: string, callbackNumber: string): string {
  if (!text) return text;
  let result = text;
  result = result.replace(/\{\{lead_name\}\}/g, lead?.name || '');
  result = result.replace(/\{\{agent_name\}\}/g, agentName || '');
  result = result.replace(/\{\{callback_number\}\}/g, callbackNumber || '');
  return result;
}

const SendEmailModal: React.FC<SendEmailModalProps> = ({
  isOpen,
  onClose,
  lead,
  setToast,
}) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [sending, setSending] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [agentName, setAgentName] = useState('');
  const [callbackNumber, setCallbackNumber] = useState('');
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [isEditingSubject, setIsEditingSubject] = useState(false);
  const [isEditingBody, setIsEditingBody] = useState(false);
  const subjectRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId('');
    setLoading(true);
    setTemplatesError(null);
    emailAPI
      .getTemplates()
      .then((list) => setTemplates(Array.isArray(list) ? list : []))
      .catch((error: any) => {
        setTemplates([]);
        setTemplatesError(extractErrorMessage(error, 'Failed to load email templates.'));
      })
      .finally(() => setLoading(false));

    Promise.all([
      userAssistantConfigService.getConfig().catch(() => null as UserAssistantConfig | null),
      authAPI.getCurrentUser().catch(() => null as any),
    ]).then(([cfg, u]) => {
      const first = (u?.first_name || '').trim();
      const last = (u?.last_name || '').trim();
      const fullName = [first, last].filter(Boolean).join(' ').trim();
      setAgentName(fullName || (cfg?.agent_name || '').trim() || (u?.email || '').trim() || '');
      setCallbackNumber((u?.phone || '').trim());
    }).catch(() => {});
  }, [isOpen]);

  const selectedTemplate = templates.find((t) => t.id === selectedId);

  const prevSelectedIdRef = useRef('');
  useEffect(() => {
    if (selectedId && selectedId !== prevSelectedIdRef.current) {
      const tpl = templates.find((t) => t.id === selectedId);
      if (tpl) {
        setEditedSubject(fillPlaceholders(tpl.subject, lead, agentName, callbackNumber));
        setEditedBody(fillPlaceholders(tpl.body, lead, agentName, callbackNumber));
      }
      setIsEditingSubject(false);
      setIsEditingBody(false);
    }
    if (!selectedId) {
      setEditedSubject('');
      setEditedBody('');
      setIsEditingSubject(false);
      setIsEditingBody(false);
    }
    prevSelectedIdRef.current = selectedId;
  }, [selectedId, templates, lead, agentName, callbackNumber]);

  const handleSend = async () => {
    if (!lead || !selectedId || isEmailTemplateBodyEmpty(editedBody) || !editedSubject.trim())
      return;
    setSending(true);
    try {
      const result = await emailAPI.sendEmail({
        lead_id: lead.id,
        template_id: selectedId,
        custom_subject: editedSubject.trim(),
        custom_body: editedBody.trimEnd(),
      });
      setToast({ type: 'success', message: result.message || 'Email sent successfully' });
      onClose();
    } catch (error: any) {
      const detail = String(error?.response?.data?.detail || 'Failed to send email');
      setToast({ type: 'error', message: detail });
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const noEmail = !lead?.email?.trim();

  return (
    <div className="fixed top-16 left-0 right-0 bottom-0 lg:left-56 z-50 overflow-y-auto custom-scrollbar flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-3xl rounded-xl bg-slate-800 border border-slate-600/50 shadow-xl">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            <EnvelopeIcon className="w-6 h-6 text-sky-400" />
            <h2 className="text-lg font-semibold text-white">Send email</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {noEmail && lead && (
            <p className="text-sm text-amber-300">
              This lead has no email address on file. Add an email on the lead record before sending.
            </p>
          )}
          {lead && !noEmail && (
            <p className="text-sm text-gray-300">
              Send to <span className="font-medium text-white">{lead.name}</span>
              <span className="text-gray-400"> ({lead.email})</span>
            </p>
          )}

          <div ref={dropdownRef} className="relative">
            <label id="email-template-label" className="block text-sm font-medium text-gray-300 mb-1.5">
              Select template *
            </label>
            {loading ? (
              <p className="text-gray-400 text-sm">Loading templates...</p>
            ) : templatesError ? (
              <p className="text-red-300 text-sm">{templatesError}</p>
            ) : templates.length === 0 ? (
              <p className="text-gray-400 text-sm">
                No email templates yet.{' '}
                <a href="/email-templates" className="text-cyan-400 hover:underline">
                  Create one
                </a>{' '}
                first.
              </p>
            ) : (
              <>
                <button
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={dropdownOpen}
                  aria-labelledby="email-template-label"
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-left text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 flex items-center justify-between gap-2"
                >
                  <span className="truncate">
                    {selectedId
                      ? templates.find((t) => t.id === selectedId)?.name ?? 'Choose a template'
                      : 'Choose a template'}
                  </span>
                  <ChevronDownIcon
                    className={`w-5 h-5 flex-shrink-0 text-gray-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
                {dropdownOpen && (
                  <ul
                    role="listbox"
                    aria-labelledby="email-template-label"
                    className="absolute left-0 right-0 top-full mt-1 z-20 py-1 rounded-lg bg-slate-800 border border-slate-600/50 shadow-xl max-h-48 overflow-y-auto custom-scrollbar"
                  >
                    <li
                      role="option"
                      aria-selected={!selectedId}
                      onClick={() => { setSelectedId(''); setDropdownOpen(false); }}
                      className={`px-3 py-2.5 cursor-pointer text-sm truncate ${!selectedId ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-300 hover:bg-white/10'}`}
                    >
                      Choose a template
                    </li>
                    {templates.map((t) => (
                      <li
                        key={t.id}
                        role="option"
                        aria-selected={selectedId === t.id}
                        onClick={() => { setSelectedId(t.id); setDropdownOpen(false); }}
                        className={`px-3 py-2.5 cursor-pointer text-sm truncate ${selectedId === t.id ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-300 hover:bg-white/10'}`}
                      >
                        {t.name}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {selectedTemplate && (
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-600/50 bg-slate-700/30 p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-medium text-gray-400">Subject</p>
                  {isEditingSubject ? (
                    <button
                      type="button"
                      onClick={() => setIsEditingSubject(false)}
                      className="flex items-center gap-1 px-2 py-0.5 rounded bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 transition-colors text-xs font-medium"
                    >
                      <CheckIcon className="w-3.5 h-3.5" />
                      <span>Done</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingSubject(true);
                        setTimeout(() => subjectRef.current?.focus(), 50);
                      }}
                      className="flex items-center gap-1 px-2 py-0.5 rounded text-gray-400 hover:text-sky-400 hover:bg-white/10 transition-colors text-xs"
                    >
                      <PencilIcon className="w-3.5 h-3.5" />
                      <span>Edit</span>
                    </button>
                  )}
                </div>
                {isEditingSubject ? (
                  <input
                    ref={subjectRef}
                    type="text"
                    value={editedSubject}
                    onChange={(e) => setEditedSubject(e.target.value)}
                    className="w-full px-2.5 py-2 bg-white/5 border border-sky-500/30 rounded-lg text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                ) : (
                  <p className="text-sm text-gray-200">{editedSubject}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-600/50 bg-slate-700/30 p-3 min-w-0">
                  <p className="text-xs font-medium text-gray-400 mb-1">Template</p>
                  <div
                    className="text-sm text-gray-300 line-clamp-6 max-h-40 overflow-hidden email-template-readonly email-template-prose"
                    dangerouslySetInnerHTML={{ __html: selectedTemplate.body }}
                  />
                </div>
                <div className="rounded-lg border border-sky-500/30 bg-sky-900/10 p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-medium text-sky-400">Body to send</p>
                    {isEditingBody ? (
                      <button
                        type="button"
                        onClick={() => setIsEditingBody(false)}
                        className="flex items-center gap-1 px-2 py-0.5 rounded bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 transition-colors text-xs font-medium"
                      >
                        <CheckIcon className="w-3.5 h-3.5" />
                        <span>Done</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsEditingBody(true)}
                        className="flex items-center gap-1 px-2 py-0.5 rounded text-gray-400 hover:text-sky-400 hover:bg-white/10 transition-colors text-xs"
                      >
                        <PencilIcon className="w-3.5 h-3.5" />
                        <span>Edit</span>
                      </button>
                    )}
                  </div>
                  {isEditingBody ? (
                    <EmailTemplateRichEditor
                      key={`send-edit-${selectedId}`}
                      content={editedBody}
                      onChange={setEditedBody}
                      placeholder="Edit message to send…"
                    />
                  ) : (
                    <div
                      className="text-xs text-gray-200 line-clamp-6 max-h-40 overflow-hidden email-template-readonly email-template-prose"
                      dangerouslySetInnerHTML={{ __html: editedBody }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/15 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={
                sending ||
                loading ||
                !selectedId ||
                isEmailTemplateBodyEmpty(editedBody) ||
                !editedSubject.trim() ||
                templates.length === 0 ||
                !lead ||
                noEmail
              }
              className="flex-1 px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg hover:from-sky-600 hover:to-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? 'Sending...' : 'Send email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendEmailModal;
